import React, { useMemo, useRef, useState } from "react";
import addNotification from "helpers/notify";
import { matchesSearch, sortByIndex, cloneDraft, SCOPE_BIDLIGHT } from "helpers/estimatingDatabase/databaseRows";
import {
  readWorkbook,
  downloadWorkbook,
  buildFlatWorkbook,
  readFlatWorkbook,
} from "helpers/estimatingDatabase/databaseXlsx";
import { DatabaseTable, displayValue } from "./DatabaseTable";
import { RecordModal } from "./RecordModal";
import { ImportModeModal, confirmDialog } from "./DatabaseModals";

const norm = (v) => String(v === undefined || v === null ? "" : v).trim().toLowerCase();

// Everything one flat list needs on a page: the table, the add/edit modal,
// delete, and Excel in and out. Returns the action handlers for the shell
// and the element to render.
export function useListPanel({
  list,
  config,
  workspace,
  search,
  options,
  optionLabel,
  newRecord,
  extraColumns,
  workbook,
  onOpenEditor,
}) {
  const [activeKey, setActiveKey] = useState(null);
  const [modal, setModal] = useState(null);
  const [pendingImport, setPendingImport] = useState(null);
  const fileRef = useRef(null);
  const rate = workspace.rate || 1;
  const bidlight = workspace.scope === SCOPE_BIDLIGHT;

  // BaseDatabaseModel.InitializeItems sorts the grid by Company.Index (the
  // Code as an integer, 0 when it is not one).
  const visible = useMemo(
    () => sortByIndex(list.items).filter((it) => matchesSearch(it.rec, config.searchFields || [], search)),
    [list.items, config.searchFields, search]
  );

  const selectedKeys = () => {
    const ticked = list.items.filter((it) => it.selected).map((it) => it.key);
    if (ticked.length) return ticked;
    return activeKey ? [activeKey] : [];
  };

  const openModal = (m) => (onOpenEditor ? onOpenEditor(m) : setModal(m));

  const onAdd = () => openModal({ mode: "add", initial: newRecord ? newRecord(list.items) : {} });

  // BaseDatabaseModel.CloneItem: the selected row copied into the Add dialog.
  const onClone = () => {
    const it = list.items.find((x) => x.key === activeKey);
    if (!it) {
      addNotification("Select an item first", "danger");
      return;
    }
    openModal({ mode: "add", clone: true, initial: cloneDraft(it.rec, config.fields) });
  };

  const onEdit = () => {
    const key = activeKey || selectedKeys()[0];
    const it = list.items.find((x) => x.key === key);
    if (!it) {
      addNotification("Select an item first", "danger");
      return;
    }
    openModal({ mode: "edit", key, initial: it.rec });
  };

  const onDelete = () => {
    const keys = selectedKeys();
    if (!keys.length) {
      addNotification("Select an item first", "danger");
      return;
    }
    confirmDialog({
      title: "Delete",
      content: `Are you sure about deleting ${keys.length > 1 ? `these ${keys.length} items` : "this item"}? It is removed from the database when you save.`,
      okText: "Delete",
      onOk: () => {
        list.remove(keys);
        setActiveKey(null);
      },
    });
  };

  // The desktop exports every loaded item, whatever is ticked or searched.
  const exportRecs = () => sortByIndex(list.items).map((it) => it.rec);

  const onExport = () => {
    const wb = workbook
      ? workbook.build(exportRecs(), { rate })
      : buildFlatWorkbook(config.sheetName || config.title || "Data", config.fields, exportRecs(), { rate });
    downloadWorkbook(wb, `${(config.sheetName || config.title || "Data").replace(/\s+/g, "")}Data.xlsx`);
  };

  const onImport = () => fileRef.current && fileRef.current.click();

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = readWorkbook(new Uint8Array(ev.target.result));
        const records = workbook
          ? workbook.read(wb, { rate })
          : readFlatWorkbook(wb, config.sheetName, config.fields, { rate });
        if (!records.length) {
          addNotification("There is no data present in the file to import.", "danger");
          return;
        }
        setPendingImport(records);
      } catch (err) {
        addNotification("The file could not be read as an Excel workbook.", "danger");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const keyOf = config.exactImportKey
    ? (rec) => String(rec[config.keyField] === undefined || rec[config.keyField] === null ? "" : rec[config.keyField]).trim()
    : (rec) => norm(rec[config.keyField]);

  const view = (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xlsm,.xls"
        style={{ display: "none" }}
        onChange={onFile}
        data-testid="database-file-input"
      />
      <DatabaseTable
        fields={config.fields}
        items={visible}
        rate={rate}
        activeKey={activeKey}
        onRowClick={setActiveKey}
        onRowDoubleClick={(key) => {
          if (!workspace.canEdit) return;
          const it = list.items.find((x) => x.key === key);
          if (it) openModal({ mode: "edit", key, initial: it.rec });
        }}
        onToggle={(key, v) => list.select([key], v)}
        onToggleAll={(v) => list.select(visible.map((it) => it.key), v)}
        optionLabel={optionLabel}
        extraColumns={extraColumns}
        showStatus={!bidlight}
        showIsPublic={workspace.isAdmin && !bidlight}
        onIsPublic={(key, v) => list.edit(key, { isPublic: v })}
        onIsPublicAll={(v) => list.items.forEach((it) => list.edit(it.key, { isPublic: v }))}
        emptyText={
          !bidlight && list.firstLoad
            ? "The company has no records for this version yet. Switch to the BidLight database, tick the rows you want and save to copy them."
            : undefined
        }
      />
      <RecordModal
        open={!!modal}
        title={modal && modal.mode === "edit" ? `Edit ${config.title || ""}` : `Add ${config.title || ""}`}
        fields={config.fields}
        initial={modal ? modal.initial : null}
        rate={rate}
        options={options}
        others={list.items.filter((it) => !modal || it.key !== modal.key).map((it) => it.rec)}
        onCancel={() => setModal(null)}
        onSubmit={(rec) => {
          if (modal.mode === "edit") list.edit(modal.key, rec);
          // CloneItem leaves the copy unticked; AddNewItem ticks it.
          else list.add(rec, { selected: !modal.clone });
          setModal(null);
        }}
      />
      <ImportModeModal
        open={!!pendingImport}
        onCancel={() => setPendingImport(null)}
        onConfirm={(mode) => {
          list.importRecords(pendingImport, mode, keyOf);
          addNotification(`${pendingImport.length} row(s) read from the file. Save to write them.`, "success");
          setPendingImport(null);
        }}
      />
    </>
  );

  return { onAdd, onEdit, onDelete, onClone, onExport, onImport, view, activeKey, setActiveKey, visible };
}

export { displayValue };
